// Atoms primarios. Estos son los "valores raw" del estado de la app.
// Todo lo demás (tokenSegments, samples, SRT etiquetado) se deriva de estos en
// derived.ts — sin useEffect, sin sincronización manual.

import { atom } from "jotai";
import type {
  OracleTranscription,
  SpeakerProfile,
} from "../lib/types.ts";
import type { ProcessingSummary, SaveUserStateConflict } from "../platform/api";
import { listProcessings, ApiError } from "../platform/api";

// --- Datos cargados del browser ---

export const oracleJsonAtom = atom<OracleTranscription | null>(null);

/**
 * El File object del WAV cargado por el usuario en la sesión actual.
 *
 * Solo se llena cuando el usuario sube el WAV via <input type="file">.
 * Para meetings existentes abiertos desde la lista, el audio viene del
 * bucket via PAR (ver audioSrcAtom).
 *
 * El elemento <audio> prefiere este File (via URL.createObjectURL) sobre
 * la PAR del bucket — es más rápido y no expira.
 */
export const audioFileAtom = atom<File | null>(null);

/**
 * URL de PAR de download para el audio del bucket, con timestamp de expiración.
 *
 * Se llena en el flujo de continuación (meetings existentes) cuando no hay
 * File en memoria. La PAR expira en 10 minutos; el hook useAudioSrcRefresh
 * la renueva automáticamente cuando quedan menos de 1 minuto.
 *
 * audioFileAtom tiene prioridad sobre este atom en audioUrlAtom (derived.ts).
 */
export const audioSrcAtom = atom<{ url: string; expiresAt: number } | null>(null);

/**
 * Nombre normalizado del archivo de audio en el bucket: "audio.wav" o "audio.mp4".
 *
 * Se setea al crear un procesamiento nuevo (desde el filename del File subido)
 * y al cargar un meeting existente (desde meta.audioFilename).
 *
 * Lo usa useAudioSrcRefresh para pedir la PAR del archivo correcto al renovar.
 * Default "audio.wav" para retrocompatibilidad con meetings anteriores a Fase 21.
 */
export const audioFilenameAtom = atom<string>("audio.wav");

// --- Estado del usuario ---

/**
 * Mapping speakerIndex → SpeakerProfile (nombre + cargo).
 * Vacío al principio. Se va llenando en la pantalla Identify.
 *
 * Las claves pueden ser:
 *   - números >= 0 → speakers detectados por Oracle (vienen del JSON)
 *   - números < 0  → speakers agregados manualmente por el usuario
 */
export const profilesAtom = atom<Map<number, SpeakerProfile>>(new Map());

/**
 * Lista de índices negativos para los speakers que el usuario agregó a mano
 * (porque Oracle no los detectó). Los índices reales vienen del JSON y son >= 0;
 * los manuales empiezan en -1 y van bajando.
 */
export const customSpeakersAtom = atom<number[]>([]);

/**
 * Overrides manuales del usuario sobre la segmentación automática.
 * Mapping segmentIndex → speakerIndex forzado.
 * Se usa en la pantalla Review para corregir segmentos de baja confianza.
 */
export const manualOverridesAtom = atom<Map<number, number>>(new Map());

/**
 * Umbral de confianza de transcripción (0..1).
 * Segmentos con avgConfidence por debajo de este valor se muestran en
 * ReviewScreen para que el usuario los revise.
 *
 * Default: 0.6. Se persiste en meta.json y se rehidrata al abrir un
 * procesamiento existente (Fase 14).
 *
 * El Slider en ReviewScreen muestra y recibe valores en escala 0-100;
 * la conversión ocurre en ese componente.
 */
export const thresholdAtom = atom<number>(0.6);

/**
 * Versión del meta.json en el backend para conflict resolution (Fase 14).
 *
 * Se inicializa a 0 (valor para metas sin version, retrocompatible).
 * Se actualiza tras cada GET /api/processings/:id exitoso y tras cada
 * PUT /api/processings/:id/meta exitoso.
 *
 * El hook useAutoSave incluye este valor en el body del PUT para que el
 * backend detecte si otro tab/sesión guardó antes (If-Match casero).
 */
export const metaVersionAtom = atom<number>(0);

// --- Autoguardado (Fase 14) ---

export type SaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Estado visible del autoguardado para el indicador visual.
 * Manejado exclusivamente por el hook useAutoSave.
 */
export const saveStatusAtom = atom<SaveStatus>("idle");

/**
 * Datos del conflicto cuando el backend devuelve 409.
 * No-null = hay un conflicto activo esperando resolución del usuario.
 * SaveIndicator renderiza el banner de resolución cuando no es null.
 */
export const saveConflictAtom = atom<SaveUserStateConflict | null>(null);

// --- Navegación ---

export const SCREENS = [
  "meetings",
  "new-meeting",
  "identify",
  "review",
  "export",
  "viewer",
  "admin",
  "corrections",
  "structure",
  "report",
  "review-report",
] as const;
export type Screen = (typeof SCREENS)[number];
export const currentScreenAtom = atom<Screen>("meetings");

/** Fase 43 — archivo SRT que debe cargar ViewerScreen. */
export type ViewerSrtFile = "tagged.srt" | "srt-confirmed.srt";
export const viewerSrtFileAtom = atom<ViewerSrtFile>("tagged.srt");

/** Tab activo en AdminScreen (persiste en URL vía hash). */
export type AdminTab = "directory" | "glossary";
export const adminTabAtom = atom<AdminTab>("directory");

/** Tab activo en StructureScreen (persiste en URL vía hash). */
export type StructureTab = "sections" | "decisions" | "tasks";
export const structureTabAtom = atom<StructureTab>("sections");

// ---------------------------------------------------------------------------
// Procesamientos.
// ---------------------------------------------------------------------------

export const processingsListAtom = atom<ProcessingSummary[] | null>(null);
export const processingsLoadingAtom = atom<boolean>(false);
export const processingsErrorAtom = atom<string | null>(null);

/**
 * Id del procesamiento que se está abriendo o ya está abierto.
 *
 * Casos:
 *   - null: nadie está abierto (estamos en Meetings).
 *   - <string>: el usuario abrió o creó este procesamiento. Vive durante
 *     el flujo new-meeting → identify → review → export.
 *
 * Cuando se vuelve a Meetings se limpia.
 */
export const currentProcessingIdAtom = atom<string | null>(null);

/**
 * Action atom (write-only): dispara un refetch del listado.
 */
export const fetchProcessingsAtom = atom(null, async (_get, set) => {
  set(processingsLoadingAtom, true);
  set(processingsErrorAtom, null);
  try {
    const res = await listProcessings();
    set(processingsListAtom, res.processings);
  } catch (err) {
    const message =
      err instanceof ApiError
        ? err.status === 401
          ? "Sesión expirada. Recargá la página para volver a entrar."
          : err.message
        : err instanceof Error
          ? err.message
          : "Error desconocido al listar procesamientos";
    set(processingsErrorAtom, message);
  } finally {
    set(processingsLoadingAtom, false);
  }
});

/**
 * Action atom: limpia todo el estado relacionado a un meeting "abierto".
 *
 * Llamar al volver a Meetings o al empezar un new-meeting nuevo. Sin esto
 * el estado del meeting anterior persiste y se mezcla con el nuevo.
 */
export const resetMeetingDataAtom = atom(null, (_get, set) => {
  set(oracleJsonAtom, null);
  set(audioFileAtom, null);
  set(audioSrcAtom, null);
  set(audioFilenameAtom, "audio.wav");
  set(profilesAtom, new Map());
  set(customSpeakersAtom, []);
  set(manualOverridesAtom, new Map());
  set(thresholdAtom, 0.6);
  set(metaVersionAtom, 0);
  set(saveStatusAtom, "idle");
  set(saveConflictAtom, null);
  set(currentProcessingIdAtom, null);
  set(correctionDecisionsAtom, new Map()); // Fase 39
  set(speakerAssignmentsAtom, new Map()); // Fase 41
  set(structureDocAtom, null); // Fase 49
  set(structureLoadingAtom, false);
  set(structureErrorAtom, null);
  set(structureSectionEditsAtom, new Map()); // Fase 50
  set(structureDecisionEditsAtom, new Map()); // Fase 51
  set(structureTaskEditsAtom, new Map()); // Fase 52
});

// ---------------------------------------------------------------------------
// Fase 39 — Decisiones del usuario sobre correcciones IA
// ---------------------------------------------------------------------------

export type CorrectionDecision = {
  /** "accept": aplica la sugerencia de la IA tal cual. */
  /** "edit": el usuario escribió una corrección manual. */
  /** "skip": el texto original es correcto, no se modifica. */
  kind: "accept" | "edit" | "skip";
  /** Texto corregido por el usuario (solo cuando kind === "edit"). */
  correctedText?: string;
};

/**
 * Mapa de itemId → decisión del usuario.
 *
 * Cubre tanto correcciones pendientes como auto-aplicadas que el usuario
 * quiere revertir. Las claves son los `id` de `AppliedCorrection` o
 * `PendingCorrection` del reporte.
 *
 * En Fase 39 solo persiste en memoria local. Si el usuario cierra la
 * pantalla, pierde su trabajo. La persistencia llega en Fase 42 con
 * el endpoint de confirm.
 */
export const correctionDecisionsAtom = atom<
  Map<string, CorrectionDecision>
>(new Map());

// ---------------------------------------------------------------------------
// Fase 41 — Asignaciones de speakers no identificados a personas del directorio
// ---------------------------------------------------------------------------

export type SpeakerAssignment = {
  personId: string;
  personName: string;
};

/** Mapa srtLabel → asignación. Ej: "Speaker 3" → { personId, personName }. */
export const speakerAssignmentsAtom = atom<Map<string, SpeakerAssignment>>(
  new Map(),
);

// ---------------------------------------------------------------------------
// Fase 49 — StructureScreen: datos del análisis estructural P3
// ---------------------------------------------------------------------------

import type { StructureDoc } from "../lib/types.ts";

export const structureDocAtom = atom<StructureDoc | null>(null);
export const structureLoadingAtom = atom<boolean>(false);
export const structureErrorAtom = atom<string | null>(null);

// ---------------------------------------------------------------------------
// Fase 50 — Ediciones locales de secciones (timeline editable)
// ---------------------------------------------------------------------------

export type SectionEdit = {
  title?: string;
  isRelevant?: boolean;
};

/** Mapa sectionId → ediciones locales. Persiste hasta confirm en Fase 52. */
export const structureSectionEditsAtom = atom<Map<string, SectionEdit>>(
  new Map(),
);

// ---------------------------------------------------------------------------
// Fase 51 — Ediciones locales de decisiones (validación)
// ---------------------------------------------------------------------------

export type DecisionEdit = {
  kind?: "confirm" | "dismiss";
  text?: string;
};

/** Mapa decisionId → ediciones locales. Persiste hasta confirm en Fase 52. */
export const structureDecisionEditsAtom = atom<Map<string, DecisionEdit>>(
  new Map(),
);

// ---------------------------------------------------------------------------
// Fase 52 — Ediciones locales de tareas (validación)
// ---------------------------------------------------------------------------

export type TaskEdit = {
  kind?: "confirm" | "dismiss";
  text?: string;
  responsibleName?: string;
  deadline?: string;
  requiresNotification?: boolean;
};

/** Mapa taskId → ediciones locales. Persiste hasta confirm en Fase 52. */
export const structureTaskEditsAtom = atom<Map<string, TaskEdit>>(
  new Map(),
);
